import React from 'react';

// Light/dark theme for Rush pages. Stored in localStorage, falls back to
// prefers-color-scheme on first visit. Applies `.rs-dark` to <body> and to
// every `.rs` root so the CSS variables defined in rush.css cascade.

const STORAGE_KEY = 'rs-theme';

const readStored = () => {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch (e) {
    return null;
  }
};

const prefersDark = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches;

const getInitial = () => readStored() ?? (prefersDark() ? 'dark' : 'light');

let currentTheme = getInitial();
const subscribers = new Set();

const applyToDom = () => {
  if (typeof document === 'undefined') return;
  const dark = currentTheme === 'dark';
  document.body.classList.toggle('rs-dark', dark);
  document.querySelectorAll('.rs').forEach((el) => el.classList.toggle('rs-dark', dark));
};

export const setTheme = (next) => {
  if (next !== 'light' && next !== 'dark') return;
  currentTheme = next;
  try { window.localStorage.setItem(STORAGE_KEY, next); } catch (e) { /* private mode */ }
  applyToDom();
  subscribers.forEach((cb) => cb(next));
};

export const getTheme = () => currentTheme;

// React hook — re-renders when the theme changes anywhere in the app.
// Also re-applies the class on mount so newly-mounted `.rs` roots pick it up.
export const useRushTheme = () => {
  const [theme, setLocal] = React.useState(currentTheme);
  React.useEffect(() => {
    applyToDom();
    const cb = (t) => setLocal(t);
    subscribers.add(cb);
    return () => subscribers.delete(cb);
  }, []);
  return [theme, setTheme];
};

const SunIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.7" />
    <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3 3l1.4 1.4M11.6 11.6L13 13M3 13l1.4-1.4M11.6 4.4L13 3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

const MoonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M13.2 9.4a5.5 5.5 0 11-6.6-6.6 5 5 0 006.6 6.6z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
  </svg>
);

export const RushThemeToggle = ({ className = '' }) => {
  const [theme, set] = useRushTheme();
  const isDark = theme === 'dark';
  const label = `Switch to ${isDark ? 'light' : 'dark'} mode`;
  return (
    <button
      type="button"
      className={`rs-theme-toggle ${className}`.trim()}
      onClick={() => set(isDark ? 'light' : 'dark')}
      aria-label={label}
      title={label}
    >
      {isDark ? <SunIcon /> : <MoonIcon />}
    </button>
  );
};

// Apply initial theme as soon as this module loads. Auth pages that import
// rush.css transitively pull this in via App.js, so they pick up the choice
// even without rendering RushNav.
if (typeof document !== 'undefined') {
  if (document.body) applyToDom();
  else document.addEventListener('DOMContentLoaded', applyToDom);
}
